import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { ParentService } from 'src/app/admin/services/parent.service';
import { AuthService } from 'src/app/auth/auth.service';
import { PaymentService } from 'src/app/services/payment.service';
import { CancelDialogComponent } from 'src/app/shared/cancel-dialog/cancel-dialog.component';
import { CancelSubscriptionDialogComponent } from 'src/app/shared/cancel-subscription-dialog/cancel-subscription-dialog.component';
import { BusinessDataService } from '../services/data.service';
import { Business } from 'backend/interfaces/business.interface';

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit {
  subscriptionDetails = null;
  cancelationRequest = null;
  faCheckCircle = faCheckCircle;
  paymentConfirmed;
  currentUserId;
  isAdmin;
  orderForm;
  adons = [];
  businesses: Business[] = [];
  selectedBusiness: Business = null;
  legacyUserSubscription = null;
  legacyPaymentConfirmed = false;
  legacyCancelationRequest = null;
  constructor(private authService: AuthService, private paymentService: PaymentService, private dialog: MatDialog, private parentService: ParentService, private router: Router, private _snackBar: MatSnackBar, private datePipe: DatePipe, private fb: FormBuilder, private businessDataService: BusinessDataService) { }
  packages = [{
    durationInMonths: 12,
    priceInRand: 399.00,
    title: '12 Months',
    description: '',
    paymentInterval: 'annually',
    frequency: '6'
  }];
  addonPackages = [
    {
      title: 'Shareable Recommendation Card',
      packageName: 'shareableRecommendationCard',
      priceInRand: 99.00,
      description: 'Users can share this business card with other users. Enables peer-to-peer recommendations. Increases organic reach & trust'
    },
    {
      title: 'Live Booking And Quote Calendar',
      packageName: 'liveBookingAndQuoteCalendar',
      priceInRand: 99.00,
      description: 'Users can: Request online quotes, Book appointments, Schedule services. Business can: Accept / decline requests, Set availability,Sync calendar'
    },
    {
      title: 'Smart Visibility Boost',
      packageName: 'smartVisibilityBoost',
      priceInRand: 99.00,
      description: 'Business gets priority exposure in: User recommendations, Similar business suggestions. Based on: User behaviour, Category activity, Engagement'
    },
    {
      title: 'Advanced Analytics Dashboard',
      packageName: 'advancedAnalyticsDashboard',
      priceInRand: 99.00,
      description: 'Views per card, Click-throughs, Messages received, Booking requests, Recommendation count, Conversion insights'
    },
    {
      title: 'Featured Business Badge',
      packageName: 'featuredBusinessBadge',
      priceInRand: 99.00,
      description: 'Visual badge on card. Builds trust & authority. Increases click probability'
    },
  ]

  ngOnInit(): void {
    if (this.router.url.includes('admin')) {
      this.isAdmin = true;
      this.parentService.activatedParent$.subscribe(res => {
        if (res) {
          this.orderForm = this.fb.group({
            name: [res.Subscription.name_first, Validators.required],
            surname: [res.Subscription.name_last, Validators.required],
            billingDate: [res.Subscription.billing_date, Validators.required],
            expiryDate: [res.Subscription.expiry_date, Validators.required],
            email: [res.Subscription.email_address, Validators.email],
            contactNumber: [res.Subscription.cell_number, Validators.required],
            cycles: [res.Subscription.cycles, Validators.required],
            address: [res.Subscription.address, Validators.required],
            package: [res.Subscription.amount, Validators.required],
            frequency: [res.Subscription.cycles]
          });
          this.currentUserId = res.id;
          this.paymentService.subscriptionDetails.next(res.Subscription);
          this.paymentService.cancelationRequest.next(res.CancelationRequest);
          this.paymentConfirmed = res.PaymentConfirmed
        }
      })
    } else {
      this.isAdmin = false;
      this.businessDataService.businesses$.subscribe(businesses => {
        if (businesses) {
          this.businesses = businesses;
          this.selectBusiness(this.selectedBusiness
            ? businesses.find(business => business.id === this.selectedBusiness.id) || businesses[0]
            : businesses[0]);
        }
      });
      this.authService.$User.subscribe(async (res) => {
        if (res) {
          this.legacyUserSubscription = res.get('Subscription');
          this.legacyPaymentConfirmed = res.get('PaymentConfirmed');
          this.legacyCancelationRequest = res.get('CancelationRequest');
          if (this.selectedBusiness) {
            const legacyTarget = this.legacyUserSubscription
              ? this.businesses.find(business => (business.Status || '').trim().toLowerCase() === 'active')
              : null;
            this.selectBusiness(legacyTarget || this.selectedBusiness);
          }
        }
      });
    }
    this.paymentService.subscriptionDetails$.subscribe(res => {
      this.subscriptionDetails = res;
    })
    this.paymentService.cancelationRequest$.subscribe(res => {
      this.cancelationRequest = res;
    })
  }

  selectBusiness(business: Business): void {
    if (!business) {
      this.selectedBusiness = null;
      this.subscriptionDetails = null;
      this.orderForm = null;
      return;
    }

    this.selectedBusiness = business;
    this.currentUserId = business.id;
    const useLegacySubscription = this.isLegacySubscriptionBusiness(business);
    this.subscriptionDetails = business.Subscription || (useLegacySubscription ? this.legacyUserSubscription : null);
    this.paymentConfirmed = business.Subscription ? business.PaymentConfirmed : (useLegacySubscription ? this.legacyPaymentConfirmed : false);
    this.cancelationRequest = business.CancelationRequest || (useLegacySubscription ? this.legacyCancelationRequest : null);
    this.paymentService.subscriptionDetails.next(this.subscriptionDetails);
    this.paymentService.cancelationRequest.next(this.cancelationRequest);

    if (this.subscriptionDetails) {
      const subscription = this.subscriptionDetails;
      this.orderForm = this.fb.group({
        name: [subscription.name_first, Validators.required],
        surname: [subscription.name_last, Validators.required],
        billingDate: [subscription.billing_date, Validators.required],
        expiryDate: [subscription.expiry_date, Validators.required],
        email: [subscription.email_address, Validators.email],
        contactNumber: [subscription.cell_number, Validators.required],
        cycles: [subscription.cycles, Validators.required],
        address: [subscription.address, Validators.required],
        package: [subscription.amount, Validators.required],
        frequency: [subscription.cycles]
      });
    } else {
      this.orderForm = null;
    }
  }

  isLegacySubscriptionBusiness(business: Business): boolean {
    if (!business || business.Subscription || !this.legacyUserSubscription) return false;
    const firstActiveBusiness = this.businesses.find(candidate =>
      (candidate.Status || '').trim().toLowerCase() === 'active'
    );
    return firstActiveBusiness?.id === business.id;
  }

  completePayment(): void {
    this.router.navigate(['/user/onboarding'], {
      queryParams: { businessId: this.selectedBusiness.id, step: 'payment' }
    });
  }

  selectSubscriptionPackage(subscriptionOption: any): void {
    this.orderForm.controls.cycles.setValue(subscriptionOption.durationInMonths);
    this.orderForm.controls.package.setValue(subscriptionOption.priceInRand);
    this.orderForm.controls.frequency.setValue(subscriptionOption.frequency);
  }

  openCancelSubscriptionDialog(): void {
    const dialogRef = this.dialog.open(CancelSubscriptionDialogComponent, {
      data: { title: 'Cancel Subscription', question: 'Are you sure you want to cancel?' },
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.paymentService.cancelSubscription(this.currentUserId, result).then(res => {
          this.openSnackBar('Cancelation request sent to admin, we will be in contact with you soon.')
        }).catch(err => {
          console.log(err)
        })
      } else {
        this.dialog.closeAll();
      }
    });
  }

  cancel() {
    this.dialog.closeAll();
  }

  updateSubscription() {
    this.paymentService.updateSubscription(this.currentUserId, this.orderForm.value).then(res => console.log(res))
  }


  openSnackBar(message: string) {
    this._snackBar.open(message, 'OK', {
      duration: 3000
    });
  }

  handleAddOnChange(event, addOn: any) {
    const currentValue = this.orderForm.controls['package'].value;
    if (event.checked) {
      this.orderForm.controls['package'].setValue(currentValue + addOn.priceInRand);
    } else {
      this.orderForm.controls['package'].setValue(currentValue - addOn.priceInRand);
    }
    console.log(this.orderForm)
  }
  deleteCancelationRequest() {
    this.paymentService.deleteCancelationRequest(this.currentUserId).then(res => {
      this.openSnackBar('Thank you, your cancelation request was removed. We will be in contact soon.')
    }).catch(err => console.log(err));
  }
}
